import './style.css';
import { orbitalPeriod } from './engines/orbital';
import { EARTH_MASS, EARTH_RADIUS } from './engines/constants';
import { fetchISSPosition, fetchUpcomingLaunches, fetchApod, type LaunchInfo } from './lib/liveData';

// ============================================================
// 言語切り替え
// ============================================================
const langBtn = document.getElementById('langToggle') as HTMLButtonElement;
let currentLang: 'ja' | 'en' = 'ja';

function applyLang(lang: 'ja' | 'en') {
  currentLang = lang;
  document.documentElement.lang = lang;
  document.querySelectorAll<HTMLElement>('[data-ja]').forEach((el) => {
    el.textContent = lang === 'ja' ? el.getAttribute('data-ja') : el.getAttribute('data-en');
  });
  langBtn.querySelectorAll<HTMLElement>('.opt').forEach((opt) => {
    opt.classList.toggle('on', opt.getAttribute('data-lang-opt') === lang);
  });
  const footerMessageJa = document.getElementById('footerMessageJa');
  const footerMessageEn = document.getElementById('footerMessageEn');
  if (footerMessageJa && footerMessageEn) {
    (footerMessageJa as HTMLElement).style.display = lang === 'ja' ? 'block' : 'none';
    (footerMessageEn as HTMLElement).style.display = lang === 'en' ? 'block' : 'none';
  }
  renderLaunches(lastLaunches);
}
langBtn.addEventListener('click', () => applyLang(currentLang === 'ja' ? 'en' : 'ja'));

// ============================================================
// オンボーディング
// ============================================================
const ONBOARDING_KEY = 'cosmos-simulator-momo:onboarding-seen-spacedev';
const onboarding = document.getElementById('onboarding') as HTMLElement;
const onboardingDismiss = document.getElementById('onboardingDismiss') as HTMLButtonElement;
const helpBtn = document.getElementById('helpBtn') as HTMLButtonElement;
onboardingDismiss.addEventListener('click', () => {
  onboarding.classList.add('hidden');
  try {
    localStorage.setItem(ONBOARDING_KEY, '1');
  } catch {
    /* noop */
  }
});
helpBtn.addEventListener('click', () => onboarding.classList.remove('hidden'));
try {
  if (localStorage.getItem(ONBOARDING_KEY) === '1') onboarding.classList.add('hidden');
} catch {
  /* noop */
}

// ============================================================
// ISS 現在位置
// ============================================================
const issDot = document.getElementById('issDot') as HTMLElement;
const issLoadingNote = document.getElementById('issLoadingNote') as HTMLElement;
const issDataBlock = document.getElementById('issDataBlock') as HTMLElement;
const issLatLon = document.getElementById('issLatLon') as HTMLElement;
const issAltitude = document.getElementById('issAltitude') as HTMLElement;
const issVelocity = document.getElementById('issVelocity') as HTMLElement;
const issVisibility = document.getElementById('issVisibility') as HTMLElement;
const issPeriodComputed = document.getElementById('issPeriodComputed') as HTMLElement;
const issValidationNote = document.getElementById('issValidationNote') as HTMLElement;

const VISIBILITY_LABEL: Record<string, { ja: string; en: string }> = {
  daylight: { ja: '昼(日照中)', en: 'Daylight' },
  eclipsed: { ja: '夜(地球の影)', en: "Eclipsed (in Earth's shadow)" },
  visible: { ja: '夜(反射光で見える)', en: 'Visible (sunlit, seen against a dark sky)' },
};

async function loadISS() {
  const pos = await fetchISSPosition();
  if (!pos) {
    issLoadingNote.classList.add('error');
    issLoadingNote.setAttribute('data-ja', 'データを取得できませんでした');
    issLoadingNote.setAttribute('data-en', 'Unable to fetch data');
    issLoadingNote.textContent = currentLang === 'ja' ? 'データを取得できませんでした' : 'Unable to fetch data';
    retryIssBtn.style.display = 'block';
    return;
  }
  issLoadingNote.style.display = 'none';
  retryIssBtn.style.display = 'none';
  issDataBlock.style.display = 'block';
  issDot.style.display = 'block';

  const xFrac = (pos.longitude + 180) / 360;
  const yFrac = (90 - pos.latitude) / 180;
  issDot.style.left = `${xFrac * 100}%`;
  issDot.style.top = `${yFrac * 100}%`;

  issLatLon.textContent = `${pos.latitude.toFixed(2)}°, ${pos.longitude.toFixed(2)}°`;
  issAltitude.textContent = `${pos.altitudeKm.toFixed(1)} km`;
  issVelocity.textContent = `${pos.velocityKmh.toFixed(0)} km/h`;
  const vis = VISIBILITY_LABEL[pos.visibility] ?? { ja: pos.visibility, en: pos.visibility };
  issVisibility.textContent = currentLang === 'ja' ? vis.ja : vis.en;

  // engines/orbital を再利用して、高度から軌道周期を逆算する
  const semiMajorAxisM = EARTH_RADIUS + pos.altitudeKm * 1000;
  const periodS = orbitalPeriod(semiMajorAxisM, EARTH_MASS);
  const periodMin = periodS / 60;
  issPeriodComputed.textContent = `${periodMin.toFixed(2)} ${currentLang === 'ja' ? '分' : 'min'}`;

  const REAL_ISS_PERIOD_MIN = 92.68; // ISSの実測軌道周期（公表値、目安）
  const err = Math.abs((periodMin - REAL_ISS_PERIOD_MIN) / REAL_ISS_PERIOD_MIN) * 100;
  const noteJa = `実測値(約${REAL_ISS_PERIOD_MIN}分)との誤差 ${err.toFixed(2)}%`;
  const noteEn = `${err.toFixed(2)}% from the measured value (~${REAL_ISS_PERIOD_MIN} min)`;
  issValidationNote.setAttribute('data-ja', noteJa);
  issValidationNote.setAttribute('data-en', noteEn);
  issValidationNote.textContent = currentLang === 'ja' ? noteJa : noteEn;
}

// ============================================================
// 今後の打ち上げ
// ============================================================
const launchLoadingNote = document.getElementById('launchLoadingNote') as HTMLElement;
const launchList = document.getElementById('launchList') as HTMLElement;
let lastLaunches: LaunchInfo[] = [];
let countdownInterval: number | null = null;

function formatCountdown(netIso: string): string {
  const target = new Date(netIso).getTime();
  const now = Date.now();
  const diffMs = target - now;
  if (Number.isNaN(target)) return '—';
  if (diffMs <= 0) return currentLang === 'ja' ? '打ち上げ済み/進行中' : 'Launched / in progress';
  const totalSeconds = Math.floor(diffMs / 1000);
  const days = Math.floor(totalSeconds / 86400);
  const hours = Math.floor((totalSeconds % 86400) / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  if (days > 0) return `T-${days}${currentLang === 'ja' ? '日' : 'd'} ${hours}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
  return `T- ${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
}

function renderLaunches(launches: LaunchInfo[]) {
  launchList.innerHTML = '';
  launches.forEach((launch) => {
    const item = document.createElement('div');
    item.className = 'launch-item';

    const name = document.createElement('div');
    name.className = 'name';
    name.textContent = launch.name;

    const provider = document.createElement('div');
    provider.className = 'provider';
    provider.textContent = launch.providerName;

    const metaRow = document.createElement('div');
    metaRow.className = 'meta-row';

    const countdown = document.createElement('span');
    countdown.className = 'countdown';
    countdown.textContent = formatCountdown(launch.netIso);
    countdown.dataset.net = launch.netIso;

    const status = document.createElement('span');
    status.className = 'status-badge';
    status.textContent = launch.statusName;

    metaRow.appendChild(countdown);
    metaRow.appendChild(status);

    const location = document.createElement('div');
    location.className = 'location';
    location.textContent = launch.padLocation;

    item.appendChild(name);
    item.appendChild(provider);
    item.appendChild(metaRow);
    item.appendChild(location);
    launchList.appendChild(item);
  });
}

function tickCountdowns() {
  document.querySelectorAll<HTMLElement>('.launch-item .countdown').forEach((el) => {
    const net = el.dataset.net;
    if (net) el.textContent = formatCountdown(net);
  });
}

async function loadLaunches() {
  const launches = await fetchUpcomingLaunches(6);
  if (!launches) {
    launchLoadingNote.classList.add('error');
    launchLoadingNote.setAttribute('data-ja', 'データを取得できませんでした');
    launchLoadingNote.setAttribute('data-en', 'Unable to fetch data');
    launchLoadingNote.textContent = currentLang === 'ja' ? 'データを取得できませんでした' : 'Unable to fetch data';
    retryLaunchBtn.style.display = 'block';
    return;
  }
  launchLoadingNote.style.display = 'none';
  retryLaunchBtn.style.display = 'none';
  lastLaunches = launches;
  renderLaunches(launches);
  if (countdownInterval !== null) window.clearInterval(countdownInterval);
  countdownInterval = window.setInterval(tickCountdowns, 1000);
}

// ============================================================
// NASA APOD
// ============================================================
const apodLoadingNote = document.getElementById('apodLoadingNote') as HTMLElement;
const apodBlock = document.getElementById('apodBlock') as HTMLElement;
const apodImage = document.getElementById('apodImage') as HTMLImageElement;
const apodTitle = document.getElementById('apodTitle') as HTMLElement;
const apodDate = document.getElementById('apodDate') as HTMLElement;
const apodLink = document.getElementById('apodLink') as HTMLAnchorElement;

async function loadApod() {
  const apod = await fetchApod();
  if (!apod) {
    apodLoadingNote.classList.add('error');
    apodLoadingNote.setAttribute('data-ja', 'データを取得できませんでした');
    apodLoadingNote.setAttribute('data-en', 'Unable to fetch data');
    apodLoadingNote.textContent = currentLang === 'ja' ? 'データを取得できませんでした' : 'Unable to fetch data';
    retryApodBtn.style.display = 'block';
    return;
  }
  apodLoadingNote.style.display = 'none';
  retryApodBtn.style.display = 'none';
  apodBlock.style.display = 'block';
  apodImage.src = apod.url;
  apodTitle.textContent = apod.title;
  apodDate.textContent = apod.date;
  apodLink.href = `https://apod.nasa.gov/apod/ap${apod.date.slice(2).replace(/-/g, '')}.html`;
}

// ============================================================
// 再試行ボタン
// ============================================================
const retryIssBtn = document.getElementById('retryIssBtn') as HTMLButtonElement;
const retryLaunchBtn = document.getElementById('retryLaunchBtn') as HTMLButtonElement;
const retryApodBtn = document.getElementById('retryApodBtn') as HTMLButtonElement;

function resetLoadingNote(note: HTMLElement, btn: HTMLButtonElement) {
  note.classList.remove('error');
  note.style.display = 'block';
  note.setAttribute('data-ja', '読み込み中...');
  note.setAttribute('data-en', 'Loading...');
  note.textContent = currentLang === 'ja' ? '読み込み中...' : 'Loading...';
  btn.style.display = 'none';
}

retryIssBtn.addEventListener('click', () => {
  resetLoadingNote(issLoadingNote, retryIssBtn);
  loadISS();
});
retryLaunchBtn.addEventListener('click', () => {
  resetLoadingNote(launchLoadingNote, retryLaunchBtn);
  loadLaunches();
});
retryApodBtn.addEventListener('click', () => {
  resetLoadingNote(apodLoadingNote, retryApodBtn);
  loadApod();
});

// ============================================================
// 初期化
// ============================================================
loadISS();
loadLaunches();
loadApod();

// ISSの位置は60秒ごとに更新
window.setInterval(loadISS, 60000);

// ページローダーを隠す
const pageLoader = document.getElementById('pageLoader');
if (pageLoader) {
  requestAnimationFrame(() => {
    pageLoader.classList.add('hide');
    setTimeout(() => pageLoader.remove(), 400);
  });
}

// ============================================================
// フッターメッセージ（クリックで開閉）
// ============================================================
const dedicationFooter = document.getElementById('dedicationFooter');
const footerMessageOverlay = document.getElementById('footerMessageOverlay');
const footerMessageClose = document.getElementById('footerMessageClose');
if (dedicationFooter && footerMessageOverlay && footerMessageClose) {
  dedicationFooter.addEventListener('click', () => {
    footerMessageOverlay.classList.add('show');
  });
  footerMessageClose.addEventListener('click', () => {
    footerMessageOverlay.classList.remove('show');
  });
  footerMessageOverlay.addEventListener('click', (e) => {
    if (e.target === footerMessageOverlay) footerMessageOverlay.classList.remove('show');
  });
}
